import { Injectable } from '@angular/core';
import { GridApi, ColumnState } from 'ag-grid-community';

interface GridPersistedState {
  filterModel: Record<string, unknown>;
  sortModel: { colId: string; sort: 'asc' | 'desc'; sortIndex: number | null }[];
}

interface User {
  correo_electronico: string;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class GridStateService {
  private readonly USER_KEY = 'user';

  /**
   * Get the current user's email from localStorage
   */
  private getUserEmail(): string | null {
    try {
      const userStr = localStorage.getItem(this.USER_KEY);
      if (!userStr) {
        return null;
      }
      const user = JSON.parse(userStr) as User;
      return user.correo_electronico || null;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      return null;
    }
  }

  /**
   * Generate the storage key for a grid
   */
  private getStorageKey(gridName: string): string | null {
    const email = this.getUserEmail();
    if (!email) {
      return null;
    }
    return `${email}-${gridName}`;
  }

  /**
   * Generate the storage key for a grid's toggle filters (state that lives
   * outside AG-Grid's own filterModel, e.g. server-side vigilancia toggles).
   */
  private getToggleStorageKey(gridName: string): string | null {
    const email = this.getUserEmail();
    if (!email) {
      return null;
    }
    return `${email}-${gridName}-toggles`;
  }

  /**
   * Save the current filter and sort state of a grid
   */
  public saveState(gridName: string, gridApi: GridApi): void {
    const storageKey = this.getStorageKey(gridName);
    if (!storageKey) {
      console.warn('Cannot save grid state: user email not found');
      return;
    }

    try {
      const filterModel = gridApi.getFilterModel();
      const columnState = gridApi.getColumnState();
      
      // Extract sort information from column state
      const sortModel = columnState
        .filter((col: ColumnState) => col.sort != null)
        .map((col: ColumnState) => ({
          colId: col.colId,
          sort: col.sort as 'asc' | 'desc',
          sortIndex: col.sortIndex ?? null,
        }));

      const state: GridPersistedState = {
        filterModel,
        sortModel,
      };

      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving grid state:', error);
    }
  }

  /**
   * Restore the filter and sort state of a grid
   */
  public restoreState(gridName: string, gridApi: GridApi): void {
    const storageKey = this.getStorageKey(gridName);
    if (!storageKey) {
      return;
    }

    try {
      const stateStr = localStorage.getItem(storageKey);
      if (!stateStr) {
        return;
      }

      const state = JSON.parse(stateStr) as GridPersistedState;

      // Restore filter model
      if (state.filterModel) {
        gridApi.setFilterModel(state.filterModel);
      }

      // Restore sort model
      if (state.sortModel && state.sortModel.length > 0) {
        const columnState = state.sortModel.map(sort => ({
          colId: sort.colId,
          sort: sort.sort,
          sortIndex: sort.sortIndex,
        }));
        gridApi.applyColumnState({
          state: columnState,
          defaultState: { sort: null },
        });
      }
    } catch (error) {
      console.error('Error restoring grid state:', error);
    }
  }

  /**
   * Save the current toggle-filter state of a grid (arbitrary boolean map,
   * persisted separately from the AG-Grid filter/sort model).
   */
  public saveToggleState(gridName: string, toggles: Record<string, boolean>): void {
    const storageKey = this.getToggleStorageKey(gridName);
    if (!storageKey) {
      console.warn('Cannot save toggle state: user email not found');
      return;
    }

    try {
      localStorage.setItem(storageKey, JSON.stringify(toggles));
    } catch (error) {
      console.error('Error saving toggle state:', error);
    }
  }

  /**
   * Restore the toggle-filter state of a grid, or null if none is saved.
   */
  public restoreToggleState(gridName: string): Record<string, boolean> | null {
    const storageKey = this.getToggleStorageKey(gridName);
    if (!storageKey) {
      return null;
    }

    try {
      const stateStr = localStorage.getItem(storageKey);
      if (!stateStr) {
        return null;
      }
      return JSON.parse(stateStr) as Record<string, boolean>;
    } catch (error) {
      console.error('Error restoring toggle state:', error);
      return null;
    }
  }

  /**
   * Clear the saved state for a grid
   */
  public clearState(gridName: string): void {
    const storageKey = this.getStorageKey(gridName);
    const toggleStorageKey = this.getToggleStorageKey(gridName);

    try {
      if (storageKey) {
        localStorage.removeItem(storageKey);
      }
      if (toggleStorageKey) {
        localStorage.removeItem(toggleStorageKey);
      }
    } catch (error) {
      console.error('Error clearing grid state:', error);
    }
  }
}
